import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from 'app/shared/services/user.service';
import { Observable, catchError, from, of, switchMap } from 'rxjs';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { AuthUser } from 'app/core/models/auth_user.model';
import { ChangePasswordRequest } from 'app/core/models/change_password_request.model';
import { BaseService } from '../../shared/services/base.service';
import { Router } from '@angular/router';
import { EnvironmentService } from 'app/services/environment.service';
import { StorageIntegrityService, getFabricacionesStorageConfig } from 'app/shared/security';
import { HomeDashboardStore } from 'app/modules/home/services/home-dashboard.store';

@Injectable({ providedIn: 'root' })
export class AuthService extends BaseService {
    private _authenticated: boolean = false;
    private signInData: { email: string; password: string } | null = null;

    constructor(
        public _http: HttpClient,
        public _router: Router,
        private _userService: UserService,
        private _localStorage: LocalStorageService,
        private _environmentService: EnvironmentService,
        private _storageIntegrity: StorageIntegrityService,
        private _homeDashboardStore: HomeDashboardStore
    ) {
        super(_router);
        
        // Inicializar StorageIntegrityService con configuración específica de fabricaciones
        this._storageIntegrity.initialize(getFabricacionesStorageConfig());
        
        // Escuchar eventos de seguridad
        this._storageIntegrity.onSecurityEvent((event) => {
            if (event.type === 'integrity_violation' || event.type === 'corruption_detected') {
                if (this._environmentService.isDevelopment()) {
                    console.warn('[fabricaciones.scm.web] Security event detected:', event);
                }
                this.signOut(); // Auto-logout en caso de manipulación
            }
        });
        
        // Debug información del entorno
        if (this._environmentService.isDevelopment()) {
            console.log('🔧 AuthService initialized for fabricaciones.scm.web:', {
                environment: this._environmentService.getEnvironment(),
                secretKeyAvailable: !!this._environmentService.getSecretKey(),
                storageIntegrityEnabled: true
            });
        }
    }

    // Loguea al usuario.
    signIn(email: string, password: string, captcha: string): Observable<any> {
        const url = `${this._api}/api/v1/login`;

        return this._http
            .post(url, {
                email: email,
                password: password,
                captcha: captcha
            })
            .pipe(
                switchMap((response: any) =>
                    from(
                        (async () => {
                            if (response.data != null && response.data.token) {
                                await this.loginWriteStorage(response.data);
                            }

                            return response;
                        })()
                    )
                ),
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Almacenamiento seguro de datos de autenticación
     * Utiliza StorageIntegrityService para proteger la integridad
     */
    private async loginWriteStorage(data: any): Promise<void> {
        await this._storageIntegrity.setSecureData('accessToken', data.token);
        this._localStorage.accessToken = data.token;

        this._authenticated = true;

        const user = AuthUser.createOne(data, new AuthUser());

        if (data.security_signature) {
            user.security_signature = data.security_signature;
        }

        this._userService.user = user;

        const serializedUser = JSON.stringify(user.toJson());
        await this._storageIntegrity.setSecureData('user', serializedUser);
        this._localStorage.user = serializedUser;

        if (this._environmentService.isDevelopment()) {
            console.log('✅ User authenticated and stored securely:', {
                hasToken: !!data.token,
                hasUser: !!user,
                environment: this._environmentService.getEnvironment(),
                storageIntegrity: 'enabled'
            });
        }
    }

    /** Desloguea el usuario */
    signOut(): void {
        // Set the authenticated flag to false
        this._authenticated = false;

        // Limpiar localStorage usando StorageIntegrityService
        this._storageIntegrity.clearSecureData();
        
        // Fallback: limpiar localStorage tradicional
        this._localStorage.clearAll();

        this._homeDashboardStore.clear();
        
        // Debug solo en desarrollo
        if (this._environmentService.isDevelopment()) {
            console.log('🔐 User signed out - secure storage cleared');
        }
    }

    /** Envia la solicitud para que el usuario cambie su contraseña */
    forgotPasswordRequest(email: string): Observable<any> {
        const url = `${this._api}/api/v1/password/mail`;

        return this._http
            .post(url, {
                email: email,
            })
            .pipe(
                switchMap((response: any) => {
                    return of(response);
                })
            );
    }

    changePassword(request: ChangePasswordRequest) {
        const url = `${this._api}/api/v1/password/reset`;
        return this._http.post(url, request.toJson()).pipe(
            switchMap((response: any) => {
                return of(response);
            })
        );
    }

    /** Valida si el usuario esta logueado o no */
    check(): Observable<boolean> {
        return from((async () => {
            const secureToken = await this._storageIntegrity.getSecureData('accessToken');
            const secureUser = await this._storageIntegrity.getSecureData('user');

            if (!secureToken || !secureUser) {
                if (!this._localStorage.accessToken || !this._localStorage.user) {
                    this._authenticated = false;
                    return false;
                }

                await this._storageIntegrity.setSecureData('accessToken', this._localStorage.accessToken);
                await this._storageIntegrity.setSecureData('user', this._localStorage.user.toString());

                if (this._environmentService.isDevelopment()) {
                    console.log('🔄 Migrated legacy storage to secure storage');
                }
            }

            this._authenticated = true;

            const userData = secureUser || this._localStorage.user.toString();
            this._userService.user = AuthUser.newModelFromStorage(userData);

            if (this._environmentService.isDevelopment()) {
                console.log('✅ Authentication check passed:', {
                    hasSecureToken: !!secureToken,
                    hasSecureUser: !!secureUser,
                    environment: this._environmentService.getEnvironment(),
                    storageIntegrity: 'enabled'
                });
            }

            return true;
        })());
    }

    verifyCode(code: number, email: string, password: string, captcha: string | null = null): Observable<any> {
        const url = `${this._api}/api/v1/verify-code`;
        return this._http
            .post(url, {
                email: email,
                password: password,
                verification_code: +code,
                captcha: captcha,
            })
            .pipe(
                switchMap((response: any) =>
                    from(
                        (async () => {
                            console.log('respuesta del verify code', response);

                            if (response.data != null && response.data.token) {
                                await this.loginWriteStorage(response.data);
                            }

                            return response;
                        })()
                    )
                ),
                catchError((error) => this.handleError(error))
            );
        }

    setSignInData(email: string, password: string): void {
        this.signInData = { email, password };
    }

    getSignInData(): { email: string; password: string } | null {
        return this.signInData;
    }

    clearSignInData(): void {
        this.signInData = null;
    }

}
